import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import CompetitorScoreChartTooltip from './CompetitorScoreChartTooltip';
import { useTranslation } from 'react-i18next';
const MAX_NAME_LENGTH = 12;
// Helper function to truncate text so the concurrent name doesnt get messy
const truncateText = (text, maxLength) => {
  const value = text == null ? '' : String(text);
  if (value.length > maxLength) {
    return value.substring(0, maxLength - 3) + '...';
  }
  return value;
};

const CompetitorScoreChart = ({
  competitors,
  userLabel = 'Vous',
  maxScore = 100,
}) => {
  const chartCompetitors = Array.isArray(competitors) ? competitors : [];
  // State for our custom Y-axis label tooltip
  const [yAxisTooltipContent, setYAxisTooltipContent] = useState(null);
  const [yAxisTooltipPosition, setYAxisTooltipPosition] = useState({
    x: 0,
    y: 0,
  });
  const { t } = useTranslation();
  // Function to determine color based on score
  const getColorForScore = (score) => {
    if (score >= 75) return '#44D7A8'; // Green
    if (score >= 25) return '#ff8d63'; // Yellow/Orange merged
    return '#f04912'; // Red
  };

  return (
    <div style={{ width: '100%', height: 200, position: 'relative' }}>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          layout="vertical"
          data={chartCompetitors}
          margin={{ left: 30 }}
          barSize={10}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            stroke="#e0e0e0"
            strokeWidth={1}
          />
          <XAxis
            type="number"
            domain={[0, maxScore]}
            tickCount={6}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#666', fontSize: 12 }}
          />
          <YAxis
            dataKey="name"
            type="category"
            axisLine={false}
            tickLine={false}
            width={80}
            tick={(props) => {
              const isUser = props.payload.value === userLabel;
              const originalName = props.payload.value;
              const displayedName = truncateText(originalName, MAX_NAME_LENGTH);

              // Don't show tooltip if name is not truncated
              const isTruncated = originalName.length > MAX_NAME_LENGTH;

              const handleMouseEnter = (e) => {
                if (!isTruncated) return;
                setYAxisTooltipContent(originalName);
                setYAxisTooltipPosition({ x: e.clientX, y: e.clientY });
              };

              const handleMouseLeave = () => {
                setYAxisTooltipContent(null);
              };

              return (
                <g
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <text
                    x={props.x}
                    y={props.y}
                    dy={props.dy}
                    textAnchor={props.textAnchor}
                    fill={isUser ? '#333' : '#555'}
                    fontWeight={isUser ? 'bold' : 'normal'}
                    fontSize={14}
                    style={{ cursor: isTruncated ? 'pointer' : 'default' }}
                  >
                    {t(displayedName)}
                  </text>
                </g>
              );
            }}
          />
          <RechartsTooltip
            formatter={(value) => {
              if (value === 0) {
                return [t('Non disponible'), t('Score')];
              } else {
                return [`${value}%`, t('Score')];
              }
            }}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '10px',
            }}
          />
          <Bar
            dataKey="score"
            background={{ fill: '#e0e0e0' }}
            radius={[0, 0, 0, 0]}
          >
            {chartCompetitors.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || getColorForScore(entry.score)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <CompetitorScoreChartTooltip position={yAxisTooltipPosition}>
        {yAxisTooltipContent}
      </CompetitorScoreChartTooltip>
    </div>
  );
};

export default CompetitorScoreChart;
